import type { RequestContext } from "../context/request-context.js";
import type { BscanReportRepository } from "../../repositories/operational-repositories.js";
import {
  BSCAN_DEFAULT_OBJECTIVE,
  BSCAN_LEGEND,
  areaOf,
  bscanLaudoAbbreviation,
  bscanScanModeLabel,
  formatExtent,
  formatMm,
  isBscanClosed,
  normalizeBscanReport,
  validateBscanReportCompleteness,
  type BscanDocumentModel,
  type BscanReport
} from "../../domain/ndt/bscan-report-domain.js";
import { isEmbeddableImage, type NdtDocumentCommon, type NdtReportCommon, type NdtValidationResult } from "../../domain/ndt/ndt-report-common.js";
import { formatCivilDateBR } from "../../domain/ndt/lp-consumable.js";
import { extractCatalogCode } from "../../domain/ndt/ndt-technical-catalog.js";
import { PermissionCode } from "../../permissions/permissions.js";
import type { DocumentSnapshot } from "../../infrastructure/documents/document-engine.js";
import { NdtReportServiceBase, type NdtInspectionLookup, type NdtReportSeed } from "./ndt-report-service-base.js";

export type BscanReportSeed = NdtReportSeed;

/**
 * RELATÓRIO DE ENSAIO ULTRASSÔNICO B-SCAN / C-SCAN.
 *
 * Herda da base dos END o que é igual a todos eles — número gerado uma vez,
 * contexto, salvar preservando identidade, trilha, assinatura pelo gate real
 * do PLH e bloqueio durante a revisão. Um fluxo de aprovação próprio seria o
 * "workflow paralelo exclusivo do B-Scan" que o comando proíbe.
 *
 * O que é do B-Scan é o `composeSpecific`: o DocumentModel ÚNICO que
 * alimenta a Visualização e o PDF final. As três saídas recebem exatamente o
 * mesmo objeto; nenhuma recalcula nada.
 */
export class BscanReportService extends NdtReportServiceBase<BscanReport, BscanDocumentModel> {
  constructor(repo: BscanReportRepository, identityRepo?: any, inspectionLookup?: NdtInspectionLookup) {
    super(repo, {
      documentType: "BSCAN",
      numberPrefix: "BSCAN",
      reportLabel: "Relatório B-Scan / C-Scan",
      permissions: {
        view: PermissionCode.BscanReportView,
        create: PermissionCode.BscanReportCreate,
        edit: PermissionCode.BscanReportEdit,
        validate: PermissionCode.BscanReportValidate,
        approve: PermissionCode.BscanReportApprove,
        sign: PermissionCode.BscanReportSign,
        manageSignatories: PermissionCode.BscanReportManageSignatories
      }
    }, identityRepo, inspectionLookup);
  }

  protected newReport(base: NdtReportCommon<BscanDocumentModel>, seed: BscanReportSeed | undefined): BscanReport {
    return {
      ...base,
      photos: [],
      system: "",
      inspectionLocation: "",
      modality: "",
      // O texto padrão é SUGESTÃO editável (§9): nasce preenchido para não
      // obrigar a redigitar o óbvio, e nada impede trocá-lo.
      objective: BSCAN_DEFAULT_OBJECTIVE,
      scanMode: "",
      surfaceCondition: "",
      nominalDiameter: "",
      nominalThicknessMm: null,
      technique: "",
      couplantId: null,
      couplant: "",
      thermometer: "",
      analysisSoftware: "",
      scannerManufacturer: "",
      technicalNotes: "",
      deviceAssetId: null,
      deviceManufacturer: "",
      deviceModel: "",
      deviceSerialNumber: "",
      deviceCalibrationCert: "",
      deviceCalibrationValidity: "",
      transducerAssetId: null,
      transducerManufacturer: "",
      transducerModel: "",
      transducerFrequency: "",
      transducerAngle: "",
      transducerSerialNumber: "",
      transducerDimension: "",
      calibrationBlockAssetId: null,
      calibrationBlock: "",
      calibrationBlockCert: "",
      calibrationBlockCertValidity: "",
      acceptanceCode: "",
      acceptanceReference: "",
      minDesignThicknessMm: null,
      otherTechnicalLimit: "",
      scanAreas: [],
      results: [],
      drawingReference: "",
      drawingRevision: "",
      drawingDescription: "",
      drawingMarkers: [],
      scanImages: [],
      technicalObservations: "",
      authorizedInspector: null,
      baseMaterial: seed?.baseMaterial ?? base.baseMaterial
    };
  }

  protected normalize(report: BscanReport): BscanReport { return normalizeBscanReport(report); }
  protected validateCompleteness(report: BscanReport): NdtValidationResult { return validateBscanReportCompleteness(report); }

  /**
   * Relatório FECHADO serve o modelo CONGELADO.
   *
   * A base só fazia isso para `SIGNED`. O B-Scan fecha na aprovação do PLH
   * (`FINALIZED`), e a montagem normal resolve AO VIVO cadastros e imagens de
   * assinatura: sem isto, trocar a assinatura de um Professional ou o
   * material do ativo depois da aprovação faria o PDF regerado sair
   * diferente do aprovado.
   */
  override async buildDocumentModel(context: RequestContext, id: string): Promise<BscanDocumentModel> {
    const report = await this.requireReport(context, id);
    if (isBscanClosed(report.status) && report.frozenSnapshot) return report.frozenSnapshot;
    return this.composeModel(report);
  }

  /**
   * A APROVAÇÃO DO PLH FECHA E CONGELA O B-SCAN.
   *
   * Chamado pelo ciclo central de revisão. `approvedSnapshot` é o documento
   * que o PLH efetivamente analisou — é ELE que fica, e não uma remontagem a
   * partir dos cadastros do momento.
   *
   * Idempotente: reprocessar uma aprovação já aplicada não reescreve nada.
   * Grava direto no repositório porque, neste ponto, a revisão já está
   * aprovada e o bloqueio de edição do PLH recusaria a própria gravação que
   * fecha o documento.
   */
  async finalizeOnPlhApproval(context: RequestContext, id: string, approvedSnapshot?: DocumentSnapshot | BscanDocumentModel | null): Promise<BscanReport | null> {
    const report = await this.repo.findById(context, id);
    if (!report) return null;
    const normalized = this.normalize(report);
    if (isBscanClosed(normalized.status)) return normalized;

    const model = this.modelOf(approvedSnapshot) ?? await this.composeModel(normalized);
    const now = new Date().toISOString();
    const finalized: BscanReport = {
      ...normalized,
      status: "FINALIZED",
      frozenSnapshot: model,
      updatedAt: now,
      history: [...normalized.history, { at: now, by: context.userId || "system", action: "SIGNED", detail: "Relatório fechado pela aprovação do PLH. Documento congelado." }]
    };
    await this.repo.save(context, finalized);
    return finalized;
  }

  /** O ciclo de revisão pode entregar o envelope ou só o modelo; os dois são aceitos. */
  private modelOf(snapshot: DocumentSnapshot | BscanDocumentModel | null | undefined): BscanDocumentModel | null {
    if (!snapshot) return null;
    const candidate = (snapshot as DocumentSnapshot).data ?? snapshot;
    return candidate && typeof candidate === "object" && "results" in candidate ? candidate as BscanDocumentModel : null;
  }

  protected async composeSpecific(report: BscanReport, common: NdtDocumentCommon): Promise<BscanDocumentModel> {
    const osSubOs = [report.osNumber, report.subOsNumber].filter(Boolean).join(" / ");
    const temperature = report.testTemperature.trim() ? `${report.testTemperature.trim()} °C` : "";

    const results = report.results.map(result => {
      const area = areaOf(report, result.scanAreaKey);
      return {
        item: result.item,
        areaCode: area?.code ?? "",
        identification: [area?.identification, area?.code ? `(${area.code})` : ""].filter(Boolean).join(" "),
        extent: area ? formatExtent(area) : "",
        nominalThickness: formatMm(area?.nominalThicknessMm ?? report.nominalThicknessMm),
        minDesignThickness: formatMm(area?.minDesignThicknessMm ?? report.minDesignThicknessMm),
        minimumThickness: formatMm(result.minimumThicknessMm),
        laudo: result.laudo,
        laudoAbbreviation: bscanLaudoAbbreviation(result.laudo),
        observation: result.observation
      };
    });

    // Fotos agrupadas por área, na ordem das áreas: "BS09-61 - Área de
    // varredura 500 mm". As sem área formam o grupo de registros gerais.
    const embeddablePhotos = report.photos.filter(p => isEmbeddableImage(p.image));
    const photoGroups: BscanDocumentModel["photoGroups"] = [];
    for (const area of report.scanAreas) {
      const photos = embeddablePhotos.filter(p => p.scanAreaKey === area.key);
      if (photos.length === 0) continue;
      const extent = formatExtent(area);
      photoGroups.push({
        title: [area.code, extent ? `Área de varredura ${extent}` : ""].filter(Boolean).join(" - "),
        photos: photos.map(p => ({ image: p.image, caption: p.caption || this.photoCaption(p.kind, area.code) }))
      });
    }
    const generalPhotos = embeddablePhotos.filter(p => !p.scanAreaKey);
    if (generalPhotos.length > 0) {
      photoGroups.push({
        title: "Registros gerais",
        photos: generalPhotos.map(p => ({ image: p.image, caption: p.caption || this.photoCaption(p.kind, "") }))
      });
    }

    const scanImages = report.scanImages
      .filter(i => isEmbeddableImage(i.image))
      .map(i => {
        const area = areaOf(report, i.scanAreaKey);
        const automatic = [
          area?.code,
          i.associatedThicknessMm !== null ? `registro associado à menor espessura reportada de ${formatMm(i.associatedThicknessMm)}.` : ""
        ].filter(Boolean).join(" - ");
        return { image: i.image, caption: i.caption || automatic };
      });

    const drawing = report.croqui && isEmbeddableImage(report.croqui.image)
      ? {
          image: report.croqui.image,
          fileName: report.croqui.fileName,
          reference: [report.drawingReference, report.drawingRevision ? `REV. ${report.drawingRevision}` : ""].filter(Boolean).join(" - "),
          caption: this.drawingCaption(report),
          markers: report.drawingMarkers
            .map(m => ({ label: areaOf(report, m.scanAreaKey)?.code ?? "", x: m.x, y: m.y }))
            .filter(m => m.label)
        }
      : null;

    return {
      ...common,
      title: "RELATÓRIO DE ENSAIO ULTRASSÔNICO\nB-SCAN / C-SCAN",
      subtitle: [report.clientDisplayName, report.system || report.unitArea].filter(Boolean).join(" - "),
      identificationRows: [
        ["CLIENTE", report.clientDisplayName],
        ["PROJETO", report.projectContract],
        ["TAG DO ATIVO", report.equipmentTag],
        ["UNIDADE / ÁREA", report.unitArea],
        ["TIPO DO ATIVO", report.equipmentTypeLabel],
        ["MODALIDADE", report.modality || bscanScanModeLabel(report.scanMode)],
        ["SISTEMA", report.system],
        ["OS / SUB-OS", osSubOs],
        ["DATA DO ENSAIO", formatCivilDateBR(report.testDate)],
        ["LOCAL DA INSPEÇÃO", report.inspectionLocation],
        ["INSPEÇÃO", report.inspectionId ? report.inspectionNumber : ""]
      ],
      objective: report.objective.trim() || BSCAN_DEFAULT_OBJECTIVE,
      technicalData: [
        ["METAL BASE", report.baseMaterial],
        ["CONDIÇÃO SUPERFICIAL", report.surfaceCondition],
        ["DIÂMETRO / ESPESSURA", [report.nominalDiameter, formatMm(report.nominalThicknessMm)].filter(Boolean).join(" / ")],
        ["MÉTODO", report.technique],
        ["MODALIDADE DO ENSAIO", bscanScanModeLabel(report.scanMode)],
        ["ACOPLANTE", report.couplant],
        ["TEMPERATURA", temperature],
        ["TERMÔMETRO / RASTREABILIDADE", report.thermometer],
        ["SOFTWARE DE ANÁLISE", report.analysisSoftware],
        ["FABRICANTE DO SCANNER", report.scannerManufacturer],
        ["OBSERVAÇÕES TÉCNICAS", report.technicalNotes]
      ],
      equipment: [
        ["APARELHO", [report.deviceManufacturer, report.deviceModel].filter(Boolean).join(" ")],
        ["Nº DE SÉRIE", report.deviceSerialNumber],
        ["CERT. CALIBRAÇÃO", [report.deviceCalibrationCert, report.deviceCalibrationValidity ? `validade ${formatCivilDateBR(report.deviceCalibrationValidity)}` : ""].filter(Boolean).join(" / ")],
        ["BLOCO DE CALIBRAÇÃO", report.calibrationBlock],
        ["CERT. BLOCO", [report.calibrationBlockCert, report.calibrationBlockCertValidity ? `validade ${formatCivilDateBR(report.calibrationBlockCertValidity)}` : ""].filter(Boolean).join(" / ")],
        ["PROCEDIMENTO", report.procedureRevision]
      ],
      transducer: {
        manufacturer: report.transducerManufacturer,
        model: report.transducerModel,
        frequency: report.transducerFrequency,
        angle: report.transducerAngle,
        serialNumber: report.transducerSerialNumber,
        dimension: report.transducerDimension
      },
      acceptance: [
        ["NORMA / CRITÉRIO DE ACEITAÇÃO", report.acceptanceCriteria || extractCatalogCode(report.referenceNorm)],
        ["NORMA DE REFERÊNCIA", extractCatalogCode(report.referenceNorm)],
        ["CÓDIGO", report.acceptanceCode],
        ["REFERÊNCIA / ITEM", report.acceptanceReference],
        ["ESPESSURA MÍNIMA DE PROJETO", formatMm(report.minDesignThicknessMm)],
        ["OUTRO LIMITE TÉCNICO", report.otherTechnicalLimit]
      ],
      results,
      drawing,
      photoGroups,
      scanImages,
      generalObservations: report.observations,
      technicalObservations: report.technicalObservations,
      // A frase do modelo original, montada a partir do dado — nunca fixa.
      temperatureNote: temperature ? `As espessuras registradas em relatório foram realizadas em superfície que apresentava temperatura de ${temperature}.` : "",
      bscanLegend: BSCAN_LEGEND,
      signatureColumns: await this.signatureColumns(report, common),
    };
  }

  private photoCaption(kind: string, areaCode: string): string {
    if (kind === "VISTA_GERAL") return "Vista geral do sistema";
    if (kind === "LOCAL_VARREDURA") return areaCode ? `Local da varredura - ${areaCode}` : "Local da varredura";
    if (kind === "DETALHE") return areaCode ? `Detalhe - ${areaCode}` : "Detalhe";
    return areaCode;
  }

  private drawingCaption(report: BscanReport): string {
    const codes = report.scanAreas.map(a => a.code).filter(Boolean);
    const parts = [
      report.drawingReference ? `Desenho de referência: ${report.drawingReference}${report.drawingRevision ? ` - REV. ${report.drawingRevision}` : ""}.` : "",
      report.drawingDescription,
      codes.length ? `Regiões ensaiadas identificadas pelos registros ${codes.join(" e ")}.` : ""
    ];
    return parts.filter(Boolean).join(" ");
  }

  /**
   * Colunas de RESPONSÁVEIS na ordem do modelo aprovado. INSPETOR, PLH e
   * FISCAL já vêm resolvidos pela base (nome, credencial e imagem de
   * assinatura do Professional); o INSPETOR AUTORIZADO é resolvido aqui pelo
   * mesmo caminho — nome e credencial do cadastro, nunca digitados.
   */
  private async signatureColumns(report: BscanReport, common: NdtDocumentCommon): Promise<BscanDocumentModel["signatureColumns"]> {
    const byRole = new Map(common.signatories.map(s => [s.role, s]));
    const column = (roleLabel: string, s: NdtDocumentCommon["signatories"][number] | undefined) => ({
      roleLabel,
      name: s?.name ?? "",
      functionTitle: s?.functionTitle ?? "",
      credential: s?.credential ?? "",
      signatureImage: s?.signatureImage ?? null,
      date: s?.date ?? ""
    });

    const authorized = report.authorizedInspector;
    let authorizedImage: string | null = null;
    if (authorized?.professionalId && this.identityRepo?.getProfessionalSignature) {
      try { authorizedImage = await this.identityRepo.getProfessionalSignature(authorized.professionalId); } catch { authorizedImage = null; }
    }
    const authorizedDate = authorized?.signedAt || (authorized?.professionalId ? report.issueDate : "");

    return [
      column("INSPETOR", byRole.get("INSPETOR")),
      column("APROVAÇÃO", byRole.get("PLH")),
      {
        roleLabel: "INSPETOR AUTORIZADO",
        name: authorized?.name ?? "",
        functionTitle: authorized?.functionTitle ?? "",
        credential: authorized?.credential ?? "",
        signatureImage: isEmbeddableImage(authorizedImage) ? authorizedImage : null,
        date: authorizedDate ? formatCivilDateBR(authorizedDate.slice(0, 10)) : ""
      },
      column("CLIENTE / FISCALIZAÇÃO", byRole.get("FISCAL"))
    ];
  }

}
